/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useEffect, useMemo, useState } from 'react';

import { DocumentVersionType, PageType } from '../../../algorithm/types';
import chatStyles from '../../Message.module.scss';
import { RenderedHTML } from '../RenderedHtml/RenderedHTML';
import tocStyles from '../../../DynaMed/DynamedTabContentPage.module.scss';
import { SectionNavigation } from '../../../DynaMed/SectionNavigation';
import { usePageChrome } from '../../hooks/PageChromeContext';
import { COMBINED_GUIDE_PAGE_NAME } from './documentTocGuidePageConstants';

type TocEntry = {
  id: string;
  pageId?: string;
  content: string;
  text: string;
};

type CombinedGuideToc = {
  firstGuidePageId?: string;
  tocEntries: TocEntry[];
};

function slugifyHeading(text: string, index: number) {
  const slug = text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `toc-${slug || 'section'}-${index + 1}`;
}

export function buildCombinedGuideToc(documentVersion: DocumentVersionType): CombinedGuideToc {
  const guidePages = documentVersion.guidePageIds
    .map((pageId) => documentVersion.pages[pageId])
    .filter((page): page is PageType => !!page);
  const firstGuidePageId = guidePages[0]?.id;
  const combinedContent = guidePages
    .map((page) => `<section data-guide-page-id="${page.id}">${page.content || ''}</section>`)
    .join('\n');

  if (typeof DOMParser === 'undefined') {
    return { firstGuidePageId, tocEntries: [] };
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(`<div>${combinedContent}</div>`, 'text/html');
  const tocEntries: TocEntry[] = [];
  const usedIds = new Set<string>();

  const headings = Array.from(parsed.body.querySelectorAll('h1'));

  headings.forEach((heading, index) => {
    const text = heading.textContent?.trim();
    if (!text) return;

    let id = heading.getAttribute('id') || slugifyHeading(text, index);
    while (usedIds.has(id)) {
      id = `${id}-${usedIds.size + 1}`;
    }
    usedIds.add(id);
    heading.setAttribute('id', id);

    const sectionNodes: Element[] = [heading];
    let nextNode = heading.nextElementSibling;
    while (nextNode && nextNode.tagName.toLowerCase() !== 'h1') {
      sectionNodes.push(nextNode);
      nextNode = nextNode.nextElementSibling;
    }

    tocEntries.push({
      content: sectionNodes
        .filter((node) => node !== heading)
        .map((node) => node.outerHTML)
        .join(''),
      id,
      pageId: heading.closest('[data-guide-page-id]')?.getAttribute('data-guide-page-id') || firstGuidePageId,
      text,
    });
  });

  return { firstGuidePageId, tocEntries };
}

type DocumentTocGuidePageProps = {
  allowEditing?: boolean;
  documentVersion: DocumentVersionType;
  onEditPage?: (page: PageType) => void;
};

export function DocumentTocGuidePage({ allowEditing, documentVersion, onEditPage }: DocumentTocGuidePageProps) {
  const { renderHeaderChrome } = usePageChrome();
  const { firstGuidePageId, tocEntries } = useMemo(() => buildCombinedGuideToc(documentVersion), [documentVersion]);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const selectedSection = tocEntries[selectedSectionIndex];
  const selectedSourcePage = selectedSection?.pageId ? documentVersion.pages[selectedSection.pageId] : undefined;
  const previousSection = selectedSectionIndex > 0 ? tocEntries[selectedSectionIndex - 1] : undefined;
  const nextSection = selectedSectionIndex < tocEntries.length - 1 ? tocEntries[selectedSectionIndex + 1] : undefined;
  const fallbackContent = documentVersion.guidePageIds
    .map((pageId) => documentVersion.pages[pageId]?.content || '')
    .join('\n');
  const content = selectedSection?.content || fallbackContent;

  useEffect(() => {
    setSelectedSectionIndex(0);
  }, [documentVersion.id]);

  function selectSection(index: number) {
    setSelectedSectionIndex(index);
  }

  function selectPreviousSection() {
    if (previousSection) setSelectedSectionIndex(selectedSectionIndex - 1);
  }

  function selectNextSection() {
    if (nextSection) setSelectedSectionIndex(selectedSectionIndex + 1);
  }

  return (
    <div className={tocEntries.length > 0 ? tocStyles.dynamedLayout : undefined}>
      {tocEntries.length > 0 && (
        <aside className={tocStyles.toc} aria-label="Guide table of contents">
          <div className={tocStyles.tocTitle}>Contents</div>
          <nav className={tocStyles.tocList} aria-label="Guide sections">
            {tocEntries.map((entry, index) => (
              <button
                key={entry.id}
                aria-current={entry.id === selectedSection?.id ? 'page' : undefined}
                className={`${tocStyles.tocItem} ${entry.id === selectedSection?.id ? tocStyles.tocItemActive : ''}`}
                onClick={() => selectSection(index)}
                type="button"
              >
                <span>{entry.text}</span>
              </button>
            ))}
          </nav>
        </aside>
      )}
      <div className={tocEntries.length > 0 ? tocStyles.contentColumn : undefined}>
        {selectedSection && (
          <SectionNavigation
            currentSectionName={selectedSection.text}
            headerExtra={
              allowEditing && selectedSourcePage
                ? renderHeaderChrome?.(selectedSourcePage, documentVersion, () => onEditPage?.(selectedSourcePage))
                : undefined
            }
            label={COMBINED_GUIDE_PAGE_NAME}
            nextSectionName={nextSection?.text}
            onNextSection={selectNextSection}
            onPreviousSection={selectPreviousSection}
            position="top"
            previousSectionName={previousSection?.text}
            sectionCount={tocEntries.length}
            selectedSectionIndex={selectedSectionIndex}
            isHeader
          />
        )}
        <div className={tocStyles.sectionContentFade} key={selectedSection?.id || COMBINED_GUIDE_PAGE_NAME}>
          <RenderedHTML
            className={chatStyles.pageContent}
            content={content}
            creatorMode={!!allowEditing}
            documentId={documentVersion.document.id}
            documentSlug={documentVersion.document.slug}
            documentVersionId={documentVersion.id}
            pageId={selectedSection?.pageId || firstGuidePageId}
            pageName={
              selectedSection ? `${COMBINED_GUIDE_PAGE_NAME}: ${selectedSection.text}` : COMBINED_GUIDE_PAGE_NAME
            }
          />
        </div>
        {selectedSection && (
          <SectionNavigation
            currentSectionName={selectedSection.text}
            label={COMBINED_GUIDE_PAGE_NAME}
            nextSectionName={nextSection?.text}
            onNextSection={selectNextSection}
            onPreviousSection={selectPreviousSection}
            position="bottom"
            previousSectionName={previousSection?.text}
            sectionCount={tocEntries.length}
            selectedSectionIndex={selectedSectionIndex}
          />
        )}
      </div>
    </div>
  );
}
